import { readModusInputChangeValue } from '@trimble/utilities';
import {
  Component,
  batch,
  createEffect,
  createSignal,
  on,
  Show,
} from 'solid-js';
import type { RgbColor } from 'solid-color';
import { ColorSwatch } from '../../components/ColorSwatch';
import { STOP_ROW_HEIGHT_PX } from '../../heatmap-stop-layout';
import { PaletteStopColorPicker } from './PaletteStopColorPicker';
import {
  createPaletteStopColorPickerControls,
  type PaletteStopColorPickerGroup,
} from './createPaletteStopColorPickerControls';

const PALETTE_STOP_TEXT_INPUT_CLASS =
  'palette-stop-modus-input h-7 w-full min-w-0 self-center bg-[var(--modus-wc-color-base-100,#f1f1f6)]';

export interface StopValueRowProps {
  readonly color: string;
  /** Committed stop value from the parent; seeds and resets the editable draft. */
  readonly initialValue: string;
  readonly lengthUnit: string;
  readonly testId: string;
  readonly editable?: boolean;
  readonly removeDisabled?: boolean;
  /**
   * Optional coordinator shared across the panel's colour pickers so opening
   * this row's picker closes any other open picker.
   */
  readonly colorPickerGroup?: PaletteStopColorPickerGroup;
  /** Called when the user saves a new colour from the swatch colour picker. */
  onColorChange(color: string): void;
  /** Called on Enter or blur with the committed draft value. */
  onValueCommit(value: string): void;
  onRemove(): void;
}

/**
 * Palette stop row: colour swatch and value. Read-only by default; passing
 * `editable` turns the value into an input and adds an `×` remove control.
 * Editable rows own a local draft while typing; the parent is notified only on
 * Enter or blur so row order does not change mid-edit (e.g. "-" before "-0.5").
 * In editable mode, clicking the swatch opens a colour picker. `initialValue` is
 * the committed value from the parent and resets the draft when it changes.
 */
export const StopValueRow: Component<StopValueRowProps> = (props) => {
  const [draftValue, setDraftValue] = createSignal(props.initialValue);
  const colorPicker = createPaletteStopColorPickerControls(
    () => props.colorPickerGroup
  );

  const canPickColor = (): boolean => props.editable === true;

  function handleSwatchClick(): void {
    if (!canPickColor()) return;
    colorPicker.toggleColorPicker();
  }

  function handleColorSave(rgb: RgbColor): void {
    // `saveColor` closes the picker; batch that with the parent colour update
    // so both flush in a single reactive pass.
    batch(() => {
      const hex = colorPicker.saveColor(rgb);
      props.onColorChange(hex);
    });
  }

  createEffect(
    on(
      () => props.initialValue,
      (value) => {
        setDraftValue(value);
      }
    )
  );

  // Enter commit can re-sort rows and drop focus synchronously (Solid `<For>`
  // reorder), which would otherwise fire focusout and commit a second time.
  let suppressFocusOutCommit = false;

  const displayedValue = (): string =>
    props.editable ? draftValue() : props.initialValue;

  function handleInputChange(event: Event): void {
    if (!props.editable) return;
    setDraftValue(readModusInputChangeValue(event));
  }

  function commitValue(): void {
    if (!props.editable) return;
    suppressFocusOutCommit = true;
    props.onValueCommit(draftValue());
    queueMicrotask(() => {
      suppressFocusOutCommit = false;
    });
  }

  function handleKeyDown(event: KeyboardEvent): void {
    if (!props.editable) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      commitValue();
    }
  }

  function handleFocusOut(): void {
    if (!props.editable || suppressFocusOutCommit) return;
    commitValue();
  }

  return (
    <div
      class="flex w-full items-center gap-1.5"
      style={{ height: `${String(STOP_ROW_HEIGHT_PX)}px` }}
      data-testid={props.testId}
    >
      <div class="relative shrink-0" ref={colorPicker.setSwatchAnchor}>
        <ColorSwatch
          color={props.color}
          testId={`${props.testId}-swatch`}
          onClick={canPickColor() ? handleSwatchClick : undefined}
        />
        <PaletteStopColorPicker
          show={colorPicker.showColorPicker() && canPickColor()}
          position={colorPicker.pickerPosition()}
          color={props.color}
          testId={`${props.testId}-color-picker`}
          onSave={handleColorSave}
          onClose={colorPicker.closeColorPicker}
        />
      </div>
      <div
        class="flex min-w-0 flex-1 items-center"
        style={{ height: `${String(STOP_ROW_HEIGHT_PX)}px` }}
        data-testid={`${props.testId}-value`}
        onFocusOut={handleFocusOut}
        onKeyDown={handleKeyDown}
      >
        <modus-wc-text-input
          class={`${PALETTE_STOP_TEXT_INPUT_CLASS}${
            props.editable ? ' palette-stop-modus-input--editable' : ''
          }`}
          bordered={false}
          label=""
          size="sm"
          type="text"
          value={displayedValue()}
          read-only={!props.editable}
          on:inputChange={handleInputChange}
        />
      </div>
      <span
        class="text-sm text-gray-6 shrink-0"
        data-testid={`${props.testId}-unit`}
      >
        {props.lengthUnit}
      </span>
      <Show when={props.editable}>
        <modus-wc-button
          type="button"
          color="tertiary"
          size="sm"
          class="shrink-0"
          onClick={() => props.onRemove()}
          // bool:disabled omits the attribute when false so Modus only treats the
          // button as disabled at the minimum stop count.
          // @ts-expect-error -- modus types do not support bool:disabled syntax
          bool:disabled={props.removeDisabled === true}
          attr:data-testid={`${props.testId}-remove`}
        >
          <modus-wc-icon name="close" size="sm" />
        </modus-wc-button>
      </Show>
    </div>
  );
};
